
import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { BrowserRouter } from 'react-router-dom'
import { initializeClient } from './integrations/supabase/client.ts'
import { initializeEnvironment } from './lib/env'
import { toast } from 'sonner'
import { HelmetProvider } from 'react-helmet-async'
import { AuthProvider } from './hooks/use-auth.tsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

const init = async () => {
  try {
    console.log('Starting app initialization...');
    
    // Initialize environment variables from Edge Function
    try {
      await initializeEnvironment();
      console.log('Environment initialized successfully');
    } catch (envError) {
      console.error('Environment initialization error:', envError);
      // Continue despite environment error - we have fallbacks in place
    }
    
    // Then initialize Supabase client
    try {
      await initializeClient();
      console.log('Supabase client initialized successfully');
    } catch (clientError) {
      console.error('Failed to initialize Supabase client:', clientError);
      // Continue despite client error - we'll show an appropriate error UI
    }
    
    // Render the app after initialization
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <QueryClientProvider client={queryClient}>
                <App />
              </QueryClientProvider>
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error('Failed to initialize app:', error);
    
    // Render a basic error state for the user with a non-router-dependent UI
    // No need for HelmetProvider, BrowserRouter, or AuthProvider in error state
    ReactDOM.createRoot(document.getElementById('root')!).render(
      <React.StrictMode>
        <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-md text-center">
            <h2 className="mb-4 text-xl font-semibold text-red-600">Application Error</h2>
            <p className="mb-2 text-gray-700">There was an error initializing the application.</p>
            <p className="text-sm text-gray-500">Please try refreshing the page or contact support if the issue persists.</p>
            <button 
              className="px-4 py-2 mt-4 text-white bg-blue-600 rounded hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </button>
          </div>
        </div>
      </React.StrictMode>
    );
  }
};

init();
